import {
  RangeDatePicker,
  type FocusState,
  type RangeDatePickerProperties,
} from "@jetblue/kit/date-picker/range";
import { useState } from "react";
import type { FlightSearchFormLabels } from "../../labels";
import { BestFaresToolbar } from "../../toolbar/best-fares-toolbar";
import type { BestFaresCalendarQueryInput } from "../../hooks/use-best-fares-calendar";
import { useBestFaresPickerState } from "./use-best-fares-picker-state";

export type BestFaresRangeDatePickerProperties = Pick<
  RangeDatePickerProperties,
  | "departIsRequired"
  | "endDate"
  | "isOpen"
  | "onOpenChange"
  | "labels"
  | "returnIsRequired"
  | "selectedDates"
  | "setSelectedDates"
  | "showValidation"
  | "startDate"
  | "headingSlot"
  | "footerSlot"
> &
  BestFaresCalendarQueryInput & {
    showDatePickerPricing: boolean;
    toolbarLabels: FlightSearchFormLabels["datePickerToolbar"];
  };
export const BestFaresRangeDatePicker = ({
  destination,
  isOpen,
  onOpenChange,
  origin,
  passengers,
  showDatePickerPricing,
  toolbarLabels,
  usePoints,
  ...rangeDatePickerProperties
}: BestFaresRangeDatePickerProperties) => {
  const { endDate, selectedDates, startDate } = rangeDatePickerProperties;
  const [selectedDepartDate, selectedReturnDate] = selectedDates;
  const [focusState, setFocusState] = useState<FocusState>("none");

  const {
    message,
    renderDate,
    selectedDepartAnnouncement,
    selectedPrice,
    selectedPriceDescription,
    selectedReturnAnnouncement,
  } = useBestFaresPickerState({
    destination,
    endDate,
    fareDirection: focusState === "end" ? "inbound" : "outbound",
    focusState,
    origin,
    passengers,
    selectedDepartDate,
    selectedReturnDate,
    showDatePickerPricing,
    startDate,
    toolbarLabels,
    tripType: "round-trip",
    usePoints,
  });
  const footerSlot =
    message || selectedPrice ? (
      <BestFaresToolbar message={message} selectedPrice={selectedPrice} />
    ) : undefined;
  const labels = {
    departDateDescriptionAria: selectedDepartAnnouncement,
    ...rangeDatePickerProperties.labels,
    doneAria: selectedPriceDescription
      ? `${rangeDatePickerProperties.labels.done}. ${selectedPriceDescription}`
      : undefined,
    returnDateDescriptionAria: selectedReturnAnnouncement,
  };

  return (
    <RangeDatePicker
      {...rangeDatePickerProperties}
      footerSlot={footerSlot}
      isOpen={isOpen}
      labels={labels}
      onFocusStateChange={setFocusState}
      onOpenChange={onOpenChange}
      renderDate={renderDate}
    />
  );
};
